import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import {
    getMusyncErrorMessage,
    MUSYNC_MUTATION_OPTIONS,
    musyncClient,
    useMusyncMutation,
} from '/@/renderer/api/musync/musync-client';
import {
    MusyncFeishinDownloadsCleanResponse,
    MusyncFeishinDownloadsResponse,
} from '/@/renderer/api/musync/musync-types';
import { downloadsPollIntervalMs } from '/@/renderer/features/downloads/downloads-flow';
import { toast } from '/@/shared/components/toast/toast';

export const downloadsQueryKey = ['musync', 'acquire', 'feishin-downloads'] as const;

export const useFeishinDownloads = (enabled = true) =>
    useQuery({
        enabled,
        queryFn: async ({ signal }) => {
            const { data } = await musyncClient().get<MusyncFeishinDownloadsResponse>(
                '/acquire/feishin-downloads',
                { signal },
            );
            return data;
        },
        queryKey: downloadsQueryKey,
        refetchInterval: (query) => downloadsPollIntervalMs(query.state.data?.poll_after_s),
        // A 5xx from musync shows inline instead of tripping the error boundary.
        throwOnError: false,
    });

/**
 * `POST /acquire/feishin-downloads/clean` without a body: musync hides every
 * finished batch (complete, failed, cancelled, partial) and never a live one.
 * Toasts the outcome so both the action bar and the page behave the same.
 */
export const useCleanFeishinDownloads = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMusyncMutation({
        ...MUSYNC_MUTATION_OPTIONS,
        mutationFn: async (_variables: void, client) => {
            const { data } = await client.post<MusyncFeishinDownloadsCleanResponse>(
                '/acquire/feishin-downloads/clean',
            );
            return data;
        },
        onError: (error) => {
            toast.error({ message: getMusyncErrorMessage(error) });
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey: downloadsQueryKey }),
        onSuccess: (data) => {
            const hidden = Number(data?.hidden) || 0;
            toast.success({
                message:
                    hidden > 0
                        ? t('page.downloads.cleaned', {
                              count: hidden,
                              defaultValue:
                                  hidden === 1
                                      ? 'Cleared 1 finished download'
                                      : `Cleared ${hidden} finished downloads`,
                          })
                        : t('page.downloads.cleanedNothing', {
                              defaultValue: 'Nothing to clean',
                          }),
            });
        },
        retry: false,
    });
};

/** A 409 from the retry endpoint is an expected "not now", not a broken backend. */
export const getRetryBatchErrorMessage = (error: unknown, fallback: string): string => {
    if (isAxiosError(error) && error.response?.status === 409) {
        const detail = error.response.data?.detail;
        return typeof detail === 'string' && detail ? detail : fallback;
    }
    return getMusyncErrorMessage(error);
};

/** `POST /acquire/batches/{id}/retry` — re-queue the failed transfers of a batch. */
export const useRetryDownloadBatch = (batchId: string) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    return useMusyncMutation({
        ...MUSYNC_MUTATION_OPTIONS,
        mutationFn: async (_variables: void, client) => {
            const { data } = await client.post<Record<string, unknown>>(
                `/acquire/batches/${encodeURIComponent(batchId)}/retry`,
            );
            return data;
        },
        onError: (error) => {
            toast.error({
                message: getRetryBatchErrorMessage(
                    error,
                    t('page.downloads.retryConflict', {
                        defaultValue: 'This download cannot be retried right now.',
                    }),
                ),
            });
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey: downloadsQueryKey }),
        onSuccess: () => {
            toast.success({
                message: t('page.downloads.retryStarted', {
                    defaultValue: 'Retrying the missing files',
                }),
            });
        },
        retry: false,
    });
};
